import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ArrowLeft, Calculator, DollarSign, Building2, Hammer,
  Home, TrendingUp, AlertCircle, CheckCircle, Info,
  RefreshCw, FileText, ChevronDown, ChevronUp, Sparkles,
  Edit2, RotateCcw, Plus, Trash2, AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const flagEmoji = { CA: '🇨🇦', US: '🇺🇸' };

// Format currency
const formatCurrency = (value, symbol = '$') => {
  if (value === null || value === undefined) return '-';
  const absValue = Math.abs(value);
  if (absValue >= 1000000) {
    return `${symbol}${(value / 1000000).toFixed(2)}M`;
  }
  return `${symbol}${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

// Condition options
const CONDITIONS = [
  { value: 'cosmetic', label: 'Cosmetic', description: 'Paint, minor repairs, cleaning', color: 'text-emerald-500', bg: 'bg-emerald-500/20' },
  { value: 'moderate', label: 'Moderate', description: 'Kitchen/bath updates, new flooring', color: 'text-cyan-500', bg: 'bg-cyan-500/20' },
  { value: 'major', label: 'Major', description: 'Full remodel, structural repairs', color: 'text-amber-500', bg: 'bg-amber-500/20' },
  { value: 'gut_rehab', label: 'Gut Rehab', description: 'Down to studs renovation', color: 'text-red-500', bg: 'bg-red-500/20' }
];

// Preset options per category
const PRESETS = [
  { value: 'light', label: 'Light', color: 'bg-emerald-500' },
  { value: 'standard', label: 'Standard', color: 'bg-cyan-500' },
  { value: 'heavy', label: 'Heavy', color: 'bg-amber-500' },
  { value: 'full_gut', label: 'Full Gut', color: 'bg-red-500' }
];

// Region options
const US_REGIONS = [
  { value: 'national_avg', label: 'National Average' },
  { value: 'northeast', label: 'Northeast (+25%)' },
  { value: 'southeast', label: 'Southeast (-10%)' },
  { value: 'midwest', label: 'Midwest (-15%)' },
  { value: 'southwest', label: 'Southwest (-5%)' },
  { value: 'west', label: 'West Coast (+30%)' }
];

const CA_REGIONS = [
  { value: 'national_avg', label: 'National Average' },
  { value: 'ontario', label: 'Ontario (+20%)' },
  { value: 'british_columbia', label: 'British Columbia (+35%)' },
  { value: 'alberta', label: 'Alberta (+10%)' },
  { value: 'quebec', label: 'Quebec (+5%)' },
  { value: 'prairies', label: 'Prairies (-5%)' },
  { value: 'atlantic', label: 'Atlantic (-10%)' }
];

// Editable Cell Component
const EditableCell = ({ value, onChange, isOverridden, onReset, aiDefault, typicalLow, typicalHigh }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleBlur = () => {
    setIsEditing(false);
    const numValue = parseFloat(localValue) || 0;
    if (numValue !== value) {
      onChange(numValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleBlur();
    } else if (e.key === 'Escape') {
      setLocalValue(value);
      setIsEditing(false);
    }
  };

  const isOutOfRange = typicalLow && typicalHigh && 
    (value < typicalLow * 0.5 || value > typicalHigh * 1.5);

  return (
    <div className="relative group">
      {isEditing ? (
        <Input
          type="number"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          className="w-28 h-8 bg-slate-600 border-cyan-500 text-right text-sm"
        />
      ) : (
        <div 
          onClick={() => setIsEditing(true)}
          className={`cursor-pointer px-2 py-1 rounded hover:bg-slate-600/50 transition-colors flex items-center justify-end gap-1 ${
            isOverridden ? 'bg-amber-500/10 border border-amber-500/30' : ''
          } ${isOutOfRange ? 'text-amber-400' : ''}`}
        >
          {formatCurrency(value)}
          <Edit2 className="w-3 h-3 opacity-0 group-hover:opacity-50" />
        </div>
      )}
      {isOverridden && !isEditing && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button 
                onClick={(e) => { e.stopPropagation(); onReset(); }}
                className="absolute -right-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <RotateCcw className="w-3 h-3 text-slate-400 hover:text-cyan-400" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">Reset to AI default: {formatCurrency(aiDefault)}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};

export default function RenovationEstimator() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [activeTab, setActiveTab] = useState('estimate');
  const [results, setResults] = useState(null);
  const [editedCategories, setEditedCategories] = useState({});
  const [customCategories, setCustomCategories] = useState([]);
  const [newCustomCategory, setNewCustomCategory] = useState({ label: '', low: '', high: '' });
  const [showAddCustom, setShowAddCustom] = useState(false);
  
  // Form state
  const [inputs, setInputs] = useState({
    property_name: '',
    address: '',
    country_code: user?.preferences?.default_country || 'US',
    region: 'national_avg',
    property_type: 'single_family',
    total_sqft: 1500,
    num_units: 1,
    num_bedrooms: 3,
    num_bathrooms: 2,
    year_built: '',
    overall_condition: 'moderate',
    // Room overrides
    kitchen_condition: '',
    bathroom_condition: '',
    flooring_condition: '',
    electrical_condition: '',
    plumbing_condition: '',
    hvac_condition: '',
    roof_condition: '',
    // Options
    include_contingency: true,
    contingency_percent: 15,
    include_contractor_markup: true,
    contractor_markup_percent: 20,
    // BRRRR
    after_repair_value: '',
    purchase_price: ''
  });

  const handleInputChange = (field, value) => {
    setInputs(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Auto-adjust units based on property type
    if (field === 'property_type') {
      if (value === 'single_family') {
        setInputs(prev => ({ ...prev, [field]: value, num_units: 1 }));
      } else if (value === 'small_multifamily') {
        setInputs(prev => ({ ...prev, [field]: value, num_units: prev.num_units < 2 ? 2 : prev.num_units }));
      }
    }
  };

  // Handle category value edit
  const handleCategoryEdit = useCallback((categoryId, field, newValue) => {
    setEditedCategories(prev => {
      // Get the existing override or find the original category
      const existing = prev[categoryId] || {};
      const originalCat = results?.categories?.find(c => c.id === categoryId) || {};
      
      return {
        ...prev,
        [categoryId]: {
          low_estimate: existing.low_estimate ?? originalCat.low_estimate,
          high_estimate: existing.high_estimate ?? originalCat.high_estimate,
          [field]: newValue,
          is_user_overridden: true
        }
      };
    });
  }, [results?.categories]);

  // Reset category to AI default
  const handleCategoryReset = useCallback((categoryId) => {
    setEditedCategories(prev => {
      const newState = { ...prev };
      delete newState[categoryId];
      return newState;
    });
  }, []);

  // Calculate local totals based on edits (for immediate UI feedback)
  const calculateLocalTotals = useCallback(() => {
    if (!results?.categories) return null;
    
    let baseLow = 0;
    let baseHigh = 0;
    
    // Sum up categories with local edits
    results.categories.forEach(cat => {
      const edited = editedCategories[cat.id];
      if (edited) {
        baseLow += edited.low_estimate ?? cat.low_estimate;
        baseHigh += edited.high_estimate ?? cat.high_estimate;
      } else {
        baseLow += cat.low_estimate;
        baseHigh += cat.high_estimate;
      }
    });
    
    // Add custom categories
    customCategories.forEach(cat => {
      baseLow += cat.low_estimate;
      baseHigh += cat.high_estimate;
    });
    
    const contingencyPercent = results.contingency_percent || 15;
    const markupPercent = results.contractor_markup_percent || 20;
    
    const contingencyLow = baseLow * (contingencyPercent / 100);
    const contingencyHigh = baseHigh * (contingencyPercent / 100);
    const markupLow = baseLow * (markupPercent / 100);
    const markupHigh = baseHigh * (markupPercent / 100);
    
    return {
      base_total_low: Math.round(baseLow),
      base_total_high: Math.round(baseHigh),
      contingency_low: Math.round(contingencyLow),
      contingency_high: Math.round(contingencyHigh),
      markup_low: Math.round(markupLow),
      markup_high: Math.round(markupHigh),
      total_low: Math.round(baseLow + contingencyLow + markupLow),
      total_high: Math.round(baseHigh + contingencyHigh + markupHigh)
    };
  }, [results, editedCategories, customCategories]);

  // Get display totals (local calculation for immediate feedback)
  const displayTotals = useMemo(() => {
    if (!results) return null;
    
    const localTotals = calculateLocalTotals();
    if (localTotals && (Object.keys(editedCategories).length > 0 || customCategories.length > 0)) {
      return localTotals;
    }
    
    return {
      base_total_low: results.base_total_low,
      base_total_high: results.base_total_high,
      contingency_low: results.contingency_low,
      contingency_high: results.contingency_high,
      markup_low: results.markup_low,
      markup_high: results.markup_high,
      total_low: results.total_low,
      total_high: results.total_high
    };
  }, [results, editedCategories, customCategories, calculateLocalTotals]);

  // Add custom category
  const handleAddCustomCategory = () => {
    if (!newCustomCategory.label || !newCustomCategory.low || !newCustomCategory.high) {
      toast.error('Please fill all fields for custom category');
      return;
    }
    
    const customId = `custom_${Date.now()}`;
    setCustomCategories(prev => [...prev, {
      id: customId,
      label: newCustomCategory.label,
      description: 'Custom line item',
      low_estimate: parseFloat(newCustomCategory.low),
      high_estimate: parseFloat(newCustomCategory.high)
    }]);
    
    setNewCustomCategory({ label: '', low: '', high: '' });
    setShowAddCustom(false);
    toast.success('Custom category added');
  };

  // Remove custom category
  const handleRemoveCustomCategory = (categoryId) => {
    setCustomCategories(prev => prev.filter(c => c.id !== categoryId));
  };

  // Build category overrides for API
  const buildCategoryOverrides = () => {
    return Object.entries(editedCategories).map(([id, data]) => ({
      id,
      low_estimate: data.low_estimate,
      high_estimate: data.high_estimate,
      is_user_overridden: true
    }));
  };

  const runEstimate = async () => {
    if (!inputs.property_name || !inputs.total_sqft) {
      toast.error('Please enter property name and square footage');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        ...inputs,
        total_sqft: Number(inputs.total_sqft),
        num_units: Number(inputs.num_units),
        num_bedrooms: Number(inputs.num_bedrooms),
        num_bathrooms: Number(inputs.num_bathrooms),
        year_built: inputs.year_built ? Number(inputs.year_built) : null,
        contingency_percent: Number(inputs.contingency_percent),
        contractor_markup_percent: Number(inputs.contractor_markup_percent),
        after_repair_value: inputs.after_repair_value ? Number(inputs.after_repair_value) : null,
        purchase_price: inputs.purchase_price ? Number(inputs.purchase_price) : null,
        // Clear empty string overrides
        kitchen_condition: inputs.kitchen_condition || null,
        bathroom_condition: inputs.bathroom_condition || null,
        flooring_condition: inputs.flooring_condition || null,
        electrical_condition: inputs.electrical_condition || null,
        plumbing_condition: inputs.plumbing_condition || null,
        hvac_condition: inputs.hvac_condition || null,
        roof_condition: inputs.roof_condition || null,
        // Include user overrides
        category_overrides: buildCategoryOverrides(),
        custom_categories: customCategories
      };
      
      const response = await axios.post(`${API}/renovation/estimate`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Check for missing inputs
      if (response.data.missing_inputs?.length > 0) {
        toast.error(`Missing required fields: ${response.data.missing_inputs.join(', ')}`);
        return;
      }
      
      setResults(response.data);
      setActiveTab('results');
      toast.success('Estimate generated successfully!');
    } catch (error) {
      console.error('Failed to generate estimate:', error);
      toast.error('Failed to generate estimate');
    } finally {
      setLoading(false);
    }
  };

  // Recalculate when user edits values
  const recalculateEstimate = async () => {
    if (!results) return;
    
    try {
      const payload = {
        ...inputs,
        total_sqft: Number(inputs.total_sqft),
        num_units: Number(inputs.num_units),
        num_bedrooms: Number(inputs.num_bedrooms),
        num_bathrooms: Number(inputs.num_bathrooms),
        year_built: inputs.year_built ? Number(inputs.year_built) : null,
        contingency_percent: Number(inputs.contingency_percent),
        contractor_markup_percent: Number(inputs.contractor_markup_percent),
        after_repair_value: inputs.after_repair_value ? Number(inputs.after_repair_value) : null,
        purchase_price: inputs.purchase_price ? Number(inputs.purchase_price) : null,
        kitchen_condition: inputs.kitchen_condition || null,
        bathroom_condition: inputs.bathroom_condition || null,
        flooring_condition: inputs.flooring_condition || null,
        electrical_condition: inputs.electrical_condition || null,
        plumbing_condition: inputs.plumbing_condition || null,
        hvac_condition: inputs.hvac_condition || null,
        roof_condition: inputs.roof_condition || null,
        category_overrides: buildCategoryOverrides(),
        custom_categories: customCategories
      };
      
      const response = await axios.post(`${API}/renovation/recalculate`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResults(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to recalculate:', error);
    }
  };

  // Debounced recalculate effect
  useEffect(() => {
    if (results && (Object.keys(editedCategories).length > 0 || customCategories.length > 0)) {
      const timer = setTimeout(() => {
        recalculateEstimate();
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedCategories, customCategories]);

  const regions = inputs.country_code === 'US' ? US_REGIONS : CA_REGIONS;
  const selectedCondition = CONDITIONS.find(c => c.value === inputs.overall_condition);

  // Merge results categories with local edits for display
  const getDisplayCategories = () => {
    if (!results?.categories) return [];
    
    return results.categories.map(cat => {
      const edited = editedCategories[cat.id];
      if (edited) {
        return {
          ...cat,
          low_estimate: edited.low_estimate ?? cat.low_estimate,
          high_estimate: edited.high_estimate ?? cat.high_estimate,
          is_user_overridden: true
        };
      }
      return cat;
    });
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              className="text-slate-400 hover:text-white"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Hammer className="w-6 h-6 text-amber-500" />
                Renovation Cost Estimator
              </h1>
              <p className="text-slate-400 text-sm">AI-powered estimates with manual override capability</p>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-800/50 border border-slate-700 p-1">
            <TabsTrigger value="estimate" className="data-[state=active]:bg-slate-700">
              Property Details
            </TabsTrigger>
            <TabsTrigger value="results" className="data-[state=active]:bg-slate-700" disabled={!results}>
              Cost Breakdown
            </TabsTrigger>
          </TabsList>

          {/* Input Tab */}
          <TabsContent value="estimate" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Property Details */}
              <div className="lg:col-span-2 space-y-6">
                {/* Basic Info */}
                <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Home className="w-5 h-5 text-cyan-500" />
                    Property Information
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label className="text-slate-400 text-sm">Property Name *</Label>
                      <Input
                        value={inputs.property_name}
                        onChange={(e) => handleInputChange('property_name', e.target.value)}
                        placeholder="e.g., 123 Main St Fourplex"
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-slate-400 text-sm">Country</Label>
                      <Select value={inputs.country_code} onValueChange={(v) => handleInputChange('country_code', v)}>
                        <SelectTrigger className="bg-slate-700 border-slate-600">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="US">🇺🇸 United States</SelectItem>
                          <SelectItem value="CA">🇨🇦 Canada</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="text-slate-400 text-sm">Region</Label>
                      <Select value={inputs.region} onValueChange={(v) => handleInputChange('region', v)}>
                        <SelectTrigger className="bg-slate-700 border-slate-600">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          {regions.map(r => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="text-slate-400 text-sm">Property Type</Label>
                      <Select value={inputs.property_type} onValueChange={(v) => handleInputChange('property_type', v)}>
                        <SelectTrigger className="bg-slate-700 border-slate-600">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="single_family">Single Family</SelectItem>
                          <SelectItem value="small_multifamily">Small Multifamily (2-4)</SelectItem>
                          <SelectItem value="medium_multifamily">Medium Multifamily (5-20)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="text-slate-400 text-sm">Total Sq Ft *</Label>
                      <Input
                        type="number"
                        value={inputs.total_sqft}
                        onChange={(e) => handleInputChange('total_sqft', e.target.value)}
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-slate-400 text-sm">Number of Units</Label>
                      <Input
                        type="number"
                        min="1"
                        value={inputs.num_units}
                        onChange={(e) => handleInputChange('num_units', e.target.value)}
                        className="bg-slate-700 border-slate-600"
                        disabled={inputs.property_type === 'single_family'}
                      />
                    </div>
                    
                    <div>
                      <Label className="text-slate-400 text-sm">Bedrooms</Label>
                      <Input
                        type="number"
                        value={inputs.num_bedrooms}
                        onChange={(e) => handleInputChange('num_bedrooms', e.target.value)}
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-slate-400 text-sm">Bathrooms</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={inputs.num_bathrooms}
                        onChange={(e) => handleInputChange('num_bathrooms', e.target.value)}
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-slate-400 text-sm">Year Built</Label>
                      <Input
                        type="number"
                        value={inputs.year_built}
                        onChange={(e) => handleInputChange('year_built', e.target.value)}
                        placeholder="e.g., 1975"
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                  </div>
                </div>

                {/* Condition Assessment */}
                <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Hammer className="w-5 h-5 text-amber-500" />
                    Overall Condition
                  </h3>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {CONDITIONS.map(condition => (
                      <button
                        key={condition.value}
                        onClick={() => handleInputChange('overall_condition', condition.value)}
                        className={`p-4 rounded-lg border-2 transition-all text-left ${
                          inputs.overall_condition === condition.value
                            ? `border-${condition.color.replace('text-', '')} ${condition.bg}`
                            : 'border-slate-600 hover:border-slate-500'
                        }`}
                      >
                        <div className={`font-semibold ${inputs.overall_condition === condition.value ? condition.color : ''}`}>
                          {condition.label}
                        </div>
                        <div className="text-xs text-slate-400 mt-1">{condition.description}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* BRRRR Integration */}
                <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-xl p-6 border border-amber-500/30">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-amber-500" />
                    BRRRR Analysis (Optional)
                  </h3>
                  <p className="text-sm text-slate-400 mb-4">
                    Add purchase price and ARV to see refinance scenarios and equity capture potential.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-400 text-sm">Purchase Price</Label>
                      <Input
                        type="number"
                        value={inputs.purchase_price}
                        onChange={(e) => handleInputChange('purchase_price', e.target.value)}
                        placeholder="e.g., 350000"
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-sm">After Repair Value (ARV)</Label>
                      <Input
                        type="number"
                        value={inputs.after_repair_value}
                        onChange={(e) => handleInputChange('after_repair_value', e.target.value)}
                        placeholder="e.g., 525000"
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                  </div>
                </div>

                {/* Advanced Options */}
                <div className="bg-slate-800/50 rounded-xl border border-slate-700">
                  <button
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="w-full p-4 flex items-center justify-between text-left"
                  >
                    <span className="font-medium">Advanced Options</span>
                    {showAdvanced ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  
                  {showAdvanced && (
                    <div className="p-6 pt-0 space-y-6">
                      {/* Cost Options */}
                      <div>
                        <h4 className="font-medium mb-3">Cost Adjustments</h4>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="text-sm">Include Contingency</span>
                              <p className="text-xs text-slate-400">Buffer for unexpected costs</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <Input
                                type="number"
                                value={inputs.contingency_percent}
                                onChange={(e) => handleInputChange('contingency_percent', e.target.value)}
                                className="w-20 bg-slate-700 border-slate-600"
                                disabled={!inputs.include_contingency}
                              />
                              <span className="text-sm text-slate-400">%</span>
                              <Switch
                                checked={inputs.include_contingency}
                                onCheckedChange={(checked) => handleInputChange('include_contingency', checked)}
                              />
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="text-sm">Contractor Markup</span>
                              <p className="text-xs text-slate-400">GC overhead and profit</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <Input
                                type="number"
                                value={inputs.contractor_markup_percent}
                                onChange={(e) => handleInputChange('contractor_markup_percent', e.target.value)}
                                className="w-20 bg-slate-700 border-slate-600"
                                disabled={!inputs.include_contractor_markup}
                              />
                              <span className="text-sm text-slate-400">%</span>
                              <Switch
                                checked={inputs.include_contractor_markup}
                                onCheckedChange={(checked) => handleInputChange('include_contractor_markup', checked)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Room-by-Room Overrides */}
                      <div>
                        <h4 className="font-medium mb-3">Room-by-Room Scope (Override)</h4>
                        <p className="text-xs text-slate-400 mb-3">Leave blank to use overall condition</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {['kitchen', 'bathroom', 'flooring', 'electrical', 'plumbing', 'hvac', 'roof'].map(room => (
                            <div key={room}>
                              <Label className="text-slate-400 text-xs capitalize">{room}</Label>
                              <Select 
                                value={inputs[`${room}_condition`] || 'same'} 
                                onValueChange={(v) => handleInputChange(`${room}_condition`, v === 'same' ? '' : v)}
                              >
                                <SelectTrigger className="bg-slate-700 border-slate-600 text-sm h-9">
                                  <SelectValue placeholder="Same" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-800 border-slate-700">
                                  <SelectItem value="same">Same as overall</SelectItem>
                                  {CONDITIONS.map(c => (
                                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column - Summary */}
              <div className="space-y-4">
                <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 sticky top-4">
                  <h3 className="text-lg font-semibold mb-4">Quick Summary</h3>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Property</span>
                      <span>{inputs.property_name || '-'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Size</span>
                      <span>{Number(inputs.total_sqft).toLocaleString()} sq ft</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Units</span>
                      <span>{inputs.num_units}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Condition</span>
                      <span className={selectedCondition?.color}>{selectedCondition?.label}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Region</span>
                      <span>{flagEmoji[inputs.country_code]} {regions.find(r => r.value === inputs.region)?.label?.split(' ')[0]}</span>
                    </div>
                  </div>
                  
                  <Button 
                    className="w-full bg-amber-500 hover:bg-amber-600 text-black"
                    onClick={runEstimate}
                    disabled={loading}
                  >
                    {loading ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Calculator className="w-4 h-4 mr-2" />
                    )}
                    Generate Estimate
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Results Tab */}
          <TabsContent value="results" className="space-y-6">
            {results && (
              <>
                {/* Validation Warnings */}
                {results.global_warnings?.length > 0 && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-500">Validation Warnings</p>
                        <ul className="text-sm text-slate-300 mt-1 space-y-1">
                          {results.global_warnings.map((w, i) => (
                            <li key={i}>• {w}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 rounded-xl p-6 border border-emerald-500/30">
                    <div className="text-sm text-slate-400 mb-1">Total Estimate</div>
                    <div className="text-2xl font-bold text-white">
                      {formatCurrency(displayTotals?.total_low)} - {formatCurrency(displayTotals?.total_high)}
                    </div>
                    <div className="text-sm text-slate-400 mt-2">
                      {formatCurrency(results.cost_per_sqft_low)}/sqft - {formatCurrency(results.cost_per_sqft_high)}/sqft
                    </div>
                  </div>
                  
                  <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                    <div className="text-sm text-slate-400 mb-1">Base Renovation</div>
                    <div className="text-xl font-bold">
                      {formatCurrency(displayTotals?.base_total_low)} - {formatCurrency(displayTotals?.base_total_high)}
                    </div>
                    <div className="text-sm text-slate-400 mt-2">Before contingency & markup</div>
                  </div>
                  
                  <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                    <div className="text-sm text-slate-400 mb-1">Added Costs</div>
                    <div className="text-xl font-bold">
                      {formatCurrency((displayTotals?.contingency_low || 0) + (displayTotals?.markup_low || 0))} - {formatCurrency((displayTotals?.contingency_high || 0) + (displayTotals?.markup_high || 0))}
                    </div>
                    <div className="text-xs text-slate-400 mt-2">
                      Contingency: {formatCurrency(displayTotals?.contingency_low)}-{formatCurrency(displayTotals?.contingency_high)} | Markup: {formatCurrency(displayTotals?.markup_low)}-{formatCurrency(displayTotals?.markup_high)}
                    </div>
                  </div>
                </div>

                {/* BRRRR Analysis */}
                {results.brrrr_analysis && (
                  <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-xl p-6 border border-amber-500/30">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-amber-500" />
                      BRRRR Analysis
                    </h3>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-white">
                          {formatCurrency(results.brrrr_analysis.after_repair_value)}
                        </div>
                        <div className="text-xs text-slate-400">ARV</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-white">
                          {formatCurrency(results.brrrr_analysis.all_in_cost)}
                        </div>
                        <div className="text-xs text-slate-400">All-In Cost</div>
                      </div>
                      <div className="text-center">
                        <div className={`text-2xl font-bold ${results.brrrr_analysis.equity_percent > 15 ? 'text-emerald-400' : results.brrrr_analysis.equity_percent > 5 ? 'text-amber-400' : 'text-red-400'}`}>
                          {formatCurrency(results.brrrr_analysis.equity_captured)}
                        </div>
                        <div className="text-xs text-slate-400">Equity Captured</div>
                      </div>
                      <div className="text-center">
                        <div className={`text-2xl font-bold ${results.brrrr_analysis.equity_percent > 15 ? 'text-emerald-400' : results.brrrr_analysis.equity_percent > 5 ? 'text-amber-400' : 'text-red-400'}`}>
                          {results.brrrr_analysis.equity_percent}%
                        </div>
                        <div className="text-xs text-slate-400">Equity %</div>
                      </div>
                    </div>
                    
                    {/* Refinance Scenarios */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <div className="text-sm font-medium mb-2">75% LTV Refinance</div>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-400">Loan Amount</span>
                            <span>{formatCurrency(results.brrrr_analysis.refinance_scenarios['75_ltv'].loan_amount)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Cash Out</span>
                            <span className={results.brrrr_analysis.refinance_scenarios['75_ltv'].cash_out > 0 ? 'text-emerald-400' : 'text-red-400'}>
                              {formatCurrency(results.brrrr_analysis.refinance_scenarios['75_ltv'].cash_out)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Cash Left in Deal</span>
                            <span>{formatCurrency(results.brrrr_analysis.refinance_scenarios['75_ltv'].cash_left_in_deal)}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <div className="text-sm font-medium mb-2">80% LTV Refinance</div>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-400">Loan Amount</span>
                            <span>{formatCurrency(results.brrrr_analysis.refinance_scenarios['80_ltv'].loan_amount)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Cash Out</span>
                            <span className={results.brrrr_analysis.refinance_scenarios['80_ltv'].cash_out > 0 ? 'text-emerald-400' : 'text-red-400'}>
                              {formatCurrency(results.brrrr_analysis.refinance_scenarios['80_ltv'].cash_out)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Cash Left in Deal</span>
                            <span>{formatCurrency(results.brrrr_analysis.refinance_scenarios['80_ltv'].cash_left_in_deal)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* 70% Rule Check */}
                    <div className={`mt-4 p-3 rounded-lg ${results.brrrr_analysis.meets_70_rule ? 'bg-emerald-500/20' : 'bg-amber-500/20'}`}>
                      <div className="flex items-center gap-2">
                        {results.brrrr_analysis.meets_70_rule ? (
                          <CheckCircle className="w-5 h-5 text-emerald-500" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-amber-500" />
                        )}
                        <span className="text-sm">
                          70% Rule: Max offer {formatCurrency(results.brrrr_analysis.max_offer_70_rule)}
                          {results.brrrr_analysis.meets_70_rule ? ' ✓ Meets rule' : ' - Purchase exceeds'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Editable Cost Breakdown Table */}
                <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-cyan-500" />
                      Cost Breakdown by Category
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-500/10 border border-amber-500/30 rounded">
                        <Edit2 className="w-3 h-3" />
                        User Override
                      </span>
                      <span>Click values to edit</span>
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-sm text-slate-400 border-b border-slate-700">
                          <th className="pb-3">Category</th>
                          <th className="pb-3 text-center">Preset</th>
                          <th className="pb-3 text-right">Low Estimate</th>
                          <th className="pb-3 text-right">High Estimate</th>
                          <th className="pb-3 w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {getDisplayCategories().map((item) => {
                          const edited = editedCategories[item.id];
                          const hasWarnings = item.warnings?.length > 0;
                          
                          return (
                            <tr key={item.id} className={`border-b border-slate-700/50 ${hasWarnings ? 'bg-amber-500/5' : ''}`}>
                              <td className="py-3">
                                <div className="flex items-center gap-2">
                                  <div className="font-medium">{item.label}</div>
                                  {item.is_user_overridden && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger>
                                          <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p className="text-xs">User modified value</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                  {item.is_custom && (
                                    <span className="text-xs px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 rounded">Custom</span>
                                  )}
                                </div>
                                {item.description && (
                                  <div className="text-xs text-slate-400">{item.description}</div>
                                )}
                                {hasWarnings && (
                                  <div className="flex items-center gap-1 mt-1 text-xs text-amber-400">
                                    <AlertTriangle className="w-3 h-3" />
                                    {item.warnings[0]}
                                  </div>
                                )}
                              </td>
                              <td className="py-3 text-center">
                                {item.preset && (
                                  <span className={`text-xs px-2 py-1 rounded ${
                                    PRESETS.find(p => p.value === item.preset)?.color || 'bg-slate-600'
                                  } bg-opacity-20`}>
                                    {PRESETS.find(p => p.value === item.preset)?.label || item.preset}
                                  </span>
                                )}
                              </td>
                              <td className="py-3 text-right">
                                <EditableCell 
                                  value={item.low_estimate}
                                  onChange={(v) => handleCategoryEdit(item.id, 'low_estimate', v)}
                                  isOverridden={item.is_user_overridden}
                                  onReset={() => handleCategoryReset(item.id)}
                                  aiDefault={item.ai_default_low}
                                  typicalLow={item.typical_range_low}
                                  typicalHigh={item.typical_range_high}
                                />
                              </td>
                              <td className="py-3 text-right">
                                <EditableCell 
                                  value={item.high_estimate}
                                  onChange={(v) => handleCategoryEdit(item.id, 'high_estimate', v)}
                                  isOverridden={item.is_user_overridden}
                                  onReset={() => handleCategoryReset(item.id)}
                                  aiDefault={item.ai_default_high}
                                  typicalLow={item.typical_range_low}
                                  typicalHigh={item.typical_range_high}
                                />
                              </td>
                              <td className="py-3">
                                {item.is_custom && (
                                  <button
                                    onClick={() => handleRemoveCustomCategory(item.id)}
                                    className="text-slate-400 hover:text-red-400 transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        
                        {/* Custom Categories from local state */}
                        {customCategories.filter(c => !results.categories?.find(rc => rc.id === c.id)).map((item) => (
                          <tr key={item.id} className="border-b border-slate-700/50">
                            <td className="py-3">
                              <div className="flex items-center gap-2">
                                <div className="font-medium">{item.label}</div>
                                <span className="text-xs px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 rounded">Custom</span>
                              </div>
                              {item.description && (
                                <div className="text-xs text-slate-400">{item.description}</div>
                              )}
                            </td>
                            <td className="py-3 text-center">-</td>
                            <td className="py-3 text-right">{formatCurrency(item.low_estimate)}</td>
                            <td className="py-3 text-right">{formatCurrency(item.high_estimate)}</td>
                            <td className="py-3">
                              <button
                                onClick={() => handleRemoveCustomCategory(item.id)}
                                className="text-slate-400 hover:text-red-400 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="font-bold">
                          <td className="pt-3">Base Total</td>
                          <td></td>
                          <td className="pt-3 text-right">{formatCurrency(displayTotals?.base_total_low)}</td>
                          <td className="pt-3 text-right">{formatCurrency(displayTotals?.base_total_high)}</td>
                          <td></td>
                        </tr>
                        <tr className="text-slate-400">
                          <td className="py-1">+ Contingency ({results.contingency_percent}%)</td>
                          <td></td>
                          <td className="py-1 text-right">{formatCurrency(displayTotals?.contingency_low)}</td>
                          <td className="py-1 text-right">{formatCurrency(displayTotals?.contingency_high)}</td>
                          <td></td>
                        </tr>
                        <tr className="text-slate-400">
                          <td className="py-1">+ Contractor Markup ({results.contractor_markup_percent}%)</td>
                          <td></td>
                          <td className="py-1 text-right">{formatCurrency(displayTotals?.markup_low)}</td>
                          <td className="py-1 text-right">{formatCurrency(displayTotals?.markup_high)}</td>
                          <td></td>
                        </tr>
                        <tr className="font-bold text-lg border-t border-slate-600">
                          <td className="pt-3">Total Estimate</td>
                          <td></td>
                          <td className="pt-3 text-right text-emerald-400">{formatCurrency(displayTotals?.total_low)}</td>
                          <td className="pt-3 text-right text-emerald-400">{formatCurrency(displayTotals?.total_high)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  
                  {/* Add Custom Category */}
                  <div className="mt-4 border-t border-slate-700 pt-4">
                    {showAddCustom ? (
                      <div className="flex items-end gap-3">
                        <div className="flex-1">
                          <Label className="text-slate-400 text-xs">Category Name</Label>
                          <Input
                            value={newCustomCategory.label}
                            onChange={(e) => setNewCustomCategory(prev => ({ ...prev, label: e.target.value }))}
                            placeholder="e.g., Pool Renovation"
                            className="bg-slate-700 border-slate-600 h-9"
                          />
                        </div>
                        <div className="w-28">
                          <Label className="text-slate-400 text-xs">Low ($)</Label>
                          <Input
                            type="number"
                            value={newCustomCategory.low}
                            onChange={(e) => setNewCustomCategory(prev => ({ ...prev, low: e.target.value }))}
                            placeholder="5000"
                            className="bg-slate-700 border-slate-600 h-9"
                          />
                        </div>
                        <div className="w-28">
                          <Label className="text-slate-400 text-xs">High ($)</Label>
                          <Input
                            type="number"
                            value={newCustomCategory.high}
                            onChange={(e) => setNewCustomCategory(prev => ({ ...prev, high: e.target.value }))}
                            placeholder="10000"
                            className="bg-slate-700 border-slate-600 h-9"
                          />
                        </div>
                        <Button 
                          size="sm"
                          onClick={handleAddCustomCategory}
                          className="bg-cyan-500 hover:bg-cyan-600"
                        >
                          Add
                        </Button>
                        <Button 
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setShowAddCustom(false);
                            setNewCustomCategory({ label: '', low: '', high: '' });
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setShowAddCustom(true)}
                        className="border-dashed border-slate-600 text-slate-400 hover:text-white"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Custom Category
                      </Button>
                    )}
                  </div>
                </div>

                {/* Planning considerations */}
                <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-500" />
                    Planning Considerations
                  </h3>
                  
                  <ul className="space-y-2">
                    {results.recommendations.map((rec, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-slate-400 mt-0.5">•</span>
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4 text-xs text-slate-500">
                    Estimates and planning considerations are informational only.
                    Verify costs, scope, permits, and safety requirements with
                    qualified local professionals.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 justify-end">
                  <Button 
                    variant="outline" 
                    className="border-slate-600"
                    onClick={() => setActiveTab('estimate')}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Modify Inputs
                  </Button>
                  <Button 
                    className="bg-cyan-500 hover:bg-cyan-600"
                    onClick={() => {
                      // Navigate to BRRRR calculator with pre-filled data
                      navigate('/brrrr-calculator', { 
                        state: { 
                          from_renovation: true,
                          rehab_estimate: (results.total_low + results.total_high) / 2,
                          purchase_price: inputs.purchase_price,
                          arv: inputs.after_repair_value
                        }
                      });
                    }}
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Run Full BRRRR Analysis
                  </Button>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
